import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Typography } from '@mui/material';
import { ArrowBackIcon as ArrowBack } from 'components/common/Icon';
import {
  FormationsTertiaryButton,
  FormationsGhostButton,
} from 'components/common/Buttons';
import { queryClient } from 'states/reactQueryClient';
import { ReadOnlyForm } from 'components/common/ReadOnlyForm';
import {
  BUSINESS_DESCRIPTION_LIMIT,
  companyDetailsCustomerStep1,
  companyDetailsCustomerStep2,
  companyDetailSolePropCustomerStep1,
} from 'schemas/companyDetailsSchema';
import {
  FormationsFormActions,
  FormationsFormContainer,
  FormationsFormContent,
  FormationsFormData,
  FormationsFormFields,
  IFormField,
  renderField,
} from 'components/common/FormationsForm2';
import { UIDateFormat } from 'helpers/dateTimeFormat';
import {
  ProgressTrackerGroupRequest,
  ProgressTrackerGroups,
  ProgressTrackerStages,
  ProgressTrackerStatus,
} from 'services/account';
import { Company } from 'models/company';
import { ENTITY_MAPPING } from 'constants/common';
import {
  useUpdateCompany,
  useIndustryOptions,
  useCurrentAccount,
  useUpdateAccountProgress,
} from 'hooks/api';
import { formatEIN } from 'helpers/progressTracker';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { formatEinInput } from 'components/common/FormationsForm2/FormationsTextField';
import {
  getProgressTrackerStageStatus,
  isProgressTrackerStageCompleted,
} from 'components/ProgressTracker/Incorporation/state';
import { formatNumberWithDashes } from 'helpers/text-transformer';
import { findIndustryTitleFromCode } from 'components/IndustryOptions/helpers';
import { SubtitleText } from 'components/ProgressTracker/StyledTypography';
import { STATES } from '../../../enums';
import { EinFieldTitle } from './EinFieldTitle';
import { StepInputContentContainer } from '../Components/StepInputContentContainer';

const solePropStep1Fields = [
  {
    type: FormationsFormFields.Suggested,
    name: 'suggested',
    placeholder: 'Company Name',
    label: 'Company Name',
    maxLength: 80,
  },
];

interface Step1FormProps {
  companyData: Company;
  onSubmit: (data: FormationsFormData) => Promise<void>;
  onBackClick: () => void;
}
const Step1Form = ({ onSubmit, companyData, onBackClick }: Step1FormProps) => {
  const [disabled, setDisabled] = useState<boolean>(false);
  const isScorp = companyData.entityType === 'S-CORP';
  const isSoleProp = companyData.entityType === ENTITY_MAPPING.sole_prop;
  const step1Fields: IFormField[] = [
    {
      type: FormationsFormFields.Text,
      name: 'name',
      label: 'Company Name',
      options: {
        placeholder: 'Company Name',
        autoFocus: true,
        maxLength: 80,
      },
    },
    {
      type: FormationsFormFields.DatePicker,
      name: 'incorporationDate',
      label: 'Date of Incorporation',
      options: {
        placeholder: 'MM/DD/YYYY',
      },
    },
    {
      type: FormationsFormFields.Text,
      name: 'ein',
      label: 'Employer Identification Number (EIN)',
      disabled,
      options: {
        maxLength: 10,
        placeholder: '12-3456789',
        formatInput: formatEinInput,
      },
    },
  ];
  const incorporatedStep1Fields = [
    ...step1Fields,
    {
      type: FormationsFormFields.CheckboxField,
      name: 'hasEinNo',
      label: 'I have not received my EIN yet',
    },
  ];
  let fields: IFormField[] = incorporatedStep1Fields;
  if (isSoleProp) {
    fields = solePropStep1Fields;
  } else if (isScorp) {
    fields = step1Fields;
  }

  const onChange = (data: FormationsFormData) => {
    setDisabled(data.hasEinNo);
  };

  const formInstance = useForm({
    defaultValues: {
      name: companyData?.name,
      suggested: companyData?.suggested
        ? companyData.suggested.map((item: any) => item?.name ?? item)
        : ['', '', ''],
      incorporationDate: companyData?.incorporationDate ?? null,
      ein: companyData?.ein ? formatEIN(companyData?.ein) : companyData?.ein,
      hasEinNo: companyData?.hasEinNo ?? false,
    },
    resolver: yupResolver(
      isSoleProp
        ? companyDetailSolePropCustomerStep1
        : companyDetailsCustomerStep1,
    ),
    context: { isSoleProp, isScorp },
  });
  const { handleSubmit, getValues, watch } = formInstance;
  useEffect(() => {
    onChange(getValues());
  }, [watch()]);

  return (
    <FormationsFormContainer sx={{ mt: 4 }}>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <FormationsFormContent>
          <StepInputContentContainer>
            <Grid container spacing={2} marginLeft={0.25}>
              {fields.map((field) => (
                <Grid item key={field.name} xs={12} sx={{ mb: 1 }}>
                  {renderField(field, formInstance)}
                </Grid>
              ))}
            </Grid>
          </StepInputContentContainer>
        </FormationsFormContent>
        <FormationsFormActions>
          <Grid container justifyContent="space-between">
            <FormationsGhostButton
              type="button"
              size="large"
              startIcon={<ArrowBack />}
              onClick={onBackClick}
              disabled={formInstance.formState.isSubmitting}
            >
              Back
            </FormationsGhostButton>

            <FormationsTertiaryButton
              isLoading={formInstance.formState.isSubmitting}
              type="submit"
              size="large"
              data-testid="save-step-1"
              loadingPosition="end"
            >
              Next
            </FormationsTertiaryButton>
          </Grid>
        </FormationsFormActions>
      </form>
    </FormationsFormContainer>
  );
};

interface Step2FormProps {
  companyData: Company;
  onSubmit: (data: FormationsFormData) => Promise<void>;
  onBackClick: () => void;
}
const mapState = (state: { name: string; code: string }) => ({
  name: state.name,
  value: state.code,
});
const Step2Form = ({ companyData, onSubmit, onBackClick }: Step2FormProps) => {
  const { data: industryOptionsData } = useIndustryOptions();
  const isScorp = companyData.entityType === 'S-CORP';
  const isSoleProp = companyData.entityType === ENTITY_MAPPING.sole_prop;
  const fields = [
    {
      type: FormationsFormFields.AutoComplete,
      name: 'stateOfIncorporation',
      label: 'State of Incorporation',
      options: {
        placeholder: 'Washington',
        options: STATES.map(mapState),
      },
    },
    {
      type: FormationsFormFields.AutoComplete,
      name: 'industry',
      label: 'Business Industry',
      options: {
        placeholder: '',
        options: industryOptionsData?.map(({ title, code }) => ({
          name: title,
          value: code,
        })),
      },
    },
    {
      type: FormationsFormFields.TextArea,
      name: 'description',
      label: 'Business Description',
      options: {
        placeholder: 'Description for your business',
        characterLimit: BUSINESS_DESCRIPTION_LIMIT,
      },
    },
  ];
  const formInstance = useForm({
    defaultValues: {
      stateOfIncorporation: STATES.find(
        (item) => item.code === companyData.stateOfIncorporation,
      )?.code,
      industry: industryOptionsData?.find(
        (item) => item.code === companyData.industry,
      )?.code,
      description: companyData.description,
    },
    resolver: yupResolver(companyDetailsCustomerStep2),
    context: {
      isSoleProp,
      isScorp,
    },
  });
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = formInstance;

  return (
    <FormationsFormContainer sx={{ mt: 4 }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormationsFormContent>
          <StepInputContentContainer>
            <Grid container spacing={2} marginLeft={0.25}>
              {fields.map((field) => (
                <Grid item key={field.name} xs={12} sx={{ mb: 1 }}>
                  {renderField(field, formInstance)}
                </Grid>
              ))}
            </Grid>
          </StepInputContentContainer>
        </FormationsFormContent>
        <FormationsFormActions>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <FormationsGhostButton
              type="button"
              size="large"
              startIcon={<ArrowBack />}
              onClick={onBackClick}
              disabled={isSubmitting}
              data-testid="back-step-2"
            >
              Back
            </FormationsGhostButton>
            <FormationsTertiaryButton
              isLoading={isSubmitting}
              type="submit"
              size="large"
              data-testid="save-step-2"
              loadingPosition="end"
            >
              Next
            </FormationsTertiaryButton>
          </Box>
        </FormationsFormActions>
      </form>
    </FormationsFormContainer>
  );
};

interface TParams {
  handleComplete: (status: ProgressTrackerStatus) => void;
  stageCompleted: boolean;
  companyData: Company;
  status: ProgressTrackerStatus | null;
  onBackClick: () => void;
  setActiveStep: (step: number) => void;
}

export const shouldShowReadOnly = (
  companyData: Company,
  status: ProgressTrackerStatus | null,
): boolean => {
  if (status === ProgressTrackerStatus.Completed) {
    return true;
  }
  const fields = [
    companyData.name,
    companyData.incorporationDate,
    companyData.stateOfIncorporation,
    companyData.industry,
    companyData.description,
  ];
  const allFilled = fields.every((i) => i != null);
  if (!allFilled) {
    return false;
  }
  if (status === ProgressTrackerStatus.Started && companyData.ein == null) {
    return true;
  }
  return false;
};

export const CompanyDetails = ({
  handleComplete,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  stageCompleted,
  companyData,
  status,
  onBackClick,
  setActiveStep,
}: TParams) => {
  const [formStep, setFormStep] = useState(0);
  const { t } = useTranslation();
  const isSoleProp = companyData?.entityType === ENTITY_MAPPING.sole_prop;
  const { data: industryOptionsData } = useIndustryOptions();

  const { mutateAsync: updateCompany } = useUpdateCompany({
    onSuccess: () =>
      queryClient.invalidateQueries(['getCompany', companyData?.id]),
  });

  const onStep1Submit = async (formData: Company) => {
    const companyDetails = {
      suggestedNames: isSoleProp
        ? formData?.suggested?.filter((name) => name)
        : undefined,
      name: formData?.name ?? undefined,
      incorporationDate: formData?.incorporationDate
        ? UIDateFormat(formData?.incorporationDate)
        : undefined,
      ein: formData.ein ? formatNumberWithDashes(formData.ein) : undefined,
      hasEinNo: !formData.hasEinNo,
    };
    if (formData?.hasEinNo) {
      companyDetails.ein = undefined;
    }
    if (companyData?.id) {
      await updateCompany({ id: companyData.id, data: companyDetails });
      setFormStep(1);
      if (status === null) {
        handleComplete(ProgressTrackerStatus.Started);
      }
    }
  };

  const onStep2Submit = async (formData: Company) => {
    const companyDetails = {
      industry: formData.industry,
      stateOfIncorporation: formData.stateOfIncorporation,
      description: formData.description,
    };
    if (companyData?.id) {
      await updateCompany({ id: companyData.id, data: companyDetails });
      const isCCorpOrLLC = [ENTITY_MAPPING.c_corp, ENTITY_MAPPING.llc].includes(
        companyData.entityType ?? '',
      );
      if (
        ProgressTrackerStatus.Completed !== status &&
        ((companyData.ein != null && isCCorpOrLLC) || !isCCorpOrLLC)
      ) {
        handleComplete(ProgressTrackerStatus.Completed);
      }
      setActiveStep(2);
    }
  };

  const onUpdateEin = () => {
    handleComplete(ProgressTrackerStatus.Completed);
  };

  const subHeading = isSoleProp
    ? t('progressTracker.incorporation.companyDetailSubHeadingSoleProp')
    : t('progressTracker.incorporation.companyDetailSubHeading');

  return (
    <div>
      <Typography variant="h5B" component="h5" data-test-id="company-details">
        Company Details
      </Typography>
      {shouldShowReadOnly(companyData, status) ? (
        <ReadOnlyForm
          items={[
            {
              key: 'company-name',
              title: 'Company Name',
              value:
                companyData?.name ??
                companyData?.suggested?.map(
                  (item: any) => item?.name ?? item,
                ) ??
                '',
            },
            {
              key: 'date-of-incorporation',
              title: 'Date of Incorporation',
              value: companyData?.incorporationDate,
            },
            {
              key: 'Employer-Identification-Number-(EIN)',
              title:
                !isSoleProp && companyData.ein == null ? (
                  <EinFieldTitle
                    companyData={companyData}
                    label="Employer Identification Number (EIN)"
                    onSubmitted={onUpdateEin}
                  />
                ) : (
                  'Employer Identification Number (EIN)'
                ),
              value: companyData?.ein ? formatEIN(companyData?.ein) : undefined,
            },
            {
              key: 'State-of-Incorporation',
              title: 'State of Incorporation',
              value: companyData?.stateOfIncorporation,
            },
            {
              key: 'Business-Industry',
              title: 'Business Industry',
              value: findIndustryTitleFromCode(
                industryOptionsData ?? [],
                companyData?.industry,
              ),
            },
            {
              key: 'Business-Description',
              title: 'Business Description',
              value: companyData?.description,
            },
          ]}
        />
      ) : (
        <>
          <Box
            sx={{
              display: formStep === 0 ? 'block' : 'none',
            }}
          >
            <SubtitleText variant="body1">{subHeading}</SubtitleText>
            <Step1Form
              onSubmit={onStep1Submit}
              companyData={companyData}
              onBackClick={onBackClick}
            />
          </Box>
          <Box
            sx={{
              display: formStep === 1 ? 'block' : 'none',
            }}
          >
            <SubtitleText variant="body1">
              {t('progressTracker.incorporation.whatBusinessIndustryYouAreIn')}
            </SubtitleText>
            <Step2Form
              companyData={companyData}
              onSubmit={onStep2Submit}
              onBackClick={() => {
                setFormStep(0);
              }}
            />
          </Box>
        </>
      )}
    </div>
  );
};

interface ContainerProps {
  company: Company;
  setActiveStep: (step: number) => void;
  onBackClick: () => void;
}

export const CompanyDetailsContainer = ({
  company,
  setActiveStep,
  onBackClick,
}: ContainerProps) => {
  const { currentAccount } = useCurrentAccount();
  const { mutateAsync: updateAccountProgress } = useUpdateAccountProgress(
    currentAccount?.id,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['progressTracker']);
      },
    },
  );
  const handleComplete = async (status: ProgressTrackerStatus) => {
    const requestProps: ProgressTrackerGroupRequest = {
      group: ProgressTrackerGroups.Incorporation,
      status,
      stage: ProgressTrackerStages.CompanyDetails,
    };

    await updateAccountProgress({
      progress: [requestProps],
      eventData: {
        stage: ProgressTrackerStages.CompanyDetails,
        stageStatus: status,
        entityType: company?.entityType ?? company?.legacyEntityType ?? '',
      },
    });
  };

  const stateCompleted = isProgressTrackerStageCompleted(
    currentAccount ?? null,
    ProgressTrackerStages.CompanyDetails,
  );
  const status = getProgressTrackerStageStatus(
    currentAccount ?? null,
    ProgressTrackerStages.CompanyDetails,
  );

  return (
    <CompanyDetails
      handleComplete={handleComplete}
      status={status ?? null}
      stageCompleted={stateCompleted}
      companyData={company}
      onBackClick={onBackClick}
      setActiveStep={setActiveStep}
    />
  );
};
